
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useCanvas, type Canvas } from '@/hooks/useCanvas'
import { useProjects } from '@/hooks/useProjects'
import type { ReactFlowInstance } from '@xyflow/react'
import type { CanvasActions } from '@/types/canvas'
import { usePostHog } from 'posthog-js/react'

/**
 * Canvas actions hook
 * Handles canvas operations (duplicate, delete, move, etc.)
 */
export function useCanvasActions(
  currentCanvas: Canvas | null,
  canvasRef: React.RefObject<ReactFlowInstance | null>,
  viewport?: { zoom: number },
  viewToggles?: {
    showControls: boolean
    onToggleControls: () => void
    showMiniMap: boolean
    onToggleMiniMap: () => void
  }
) {
  const navigate = useNavigate()
  const { updateCanvas, deleteCanvas, duplicateCanvas } = useCanvas()
  const { projects } = useProjects()
  const posthog = usePostHog()

  // Modal states
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [showNewCanvasDialog, setShowNewCanvasDialog] = useState(false)
  const [newCanvasName, setNewCanvasName] = useState('')

  // Zoom state - sync from viewport
  const [currentZoom, setCurrentZoom] = useState(50)

  // Sync zoom from viewport changes
  useEffect(() => {
    if (viewport) {
      const zoomPercent = Math.round((viewport.zoom || 0.5) * 100)
      setCurrentZoom(zoomPercent)
    }
  }, [viewport])

  // Navigation actions
  const handleBackToWorkspace = useCallback(() => {
    navigate({ to: '/workspace' })
  }, [navigate])

  const handleNewCanvas = useCallback(() => {
    setShowNewCanvasDialog(true)
  }, [])

  const handleNewCanvasConfirm = useCallback(() => {
    setShowNewCanvasDialog(false)
    navigate({ to: '/canvas/new' })
  }, [navigate])

  // Duplicate canvas
  const handleDuplicate = useCallback(() => {
    setShowDuplicateDialog(true)
  }, [])

  const handleDuplicateConfirm = useCallback(async () => {
    if (currentCanvas) {
      const duplicated = await duplicateCanvas(currentCanvas.uuid)
      setShowDuplicateDialog(false)
      if (duplicated) {
        posthog.capture('canvas_duplicated', {
          original_canvas_id: currentCanvas.uuid,
          new_canvas_id: duplicated.uuid,
          canvas_title: currentCanvas.title,
        })
        // Navigate to the duplicated canvas
        navigate({ to: `/canvas/${duplicated.uuid}` })
      }
    }
  }, [currentCanvas, duplicateCanvas, navigate, posthog])

  // Rename canvas
  const handleRename = useCallback(() => {
    setNewCanvasName(currentCanvas?.title || '')
    setShowRenameDialog(true)
  }, [currentCanvas])

  const handleRenameConfirm = useCallback(async () => {
    if (!currentCanvas || !newCanvasName.trim()) return

    try {
      const updated = await updateCanvas(currentCanvas.uuid, { title: newCanvasName.trim() })
      setShowRenameDialog(false)
      if (updated) {
        posthog.capture('canvas_renamed', {
          canvas_id: currentCanvas.uuid,
          old_title: currentCanvas.title,
          new_title: newCanvasName.trim(),
        })
      }
      return updated
    } catch (error) {
      console.error('Failed to rename canvas:', error)
      return null
    }
  }, [currentCanvas, newCanvasName, updateCanvas, posthog])

  // Move to project
  const handleMoveToProject = useCallback(async (projectId: string | null) => {
    if (currentCanvas) {
      try {
        await updateCanvas(currentCanvas.uuid, { projectId })
      } catch (error) {
        console.error('Failed to move canvas:', error)
      }
    }
  }, [currentCanvas, updateCanvas])

  // Zoom to fit - fit all nodes in viewport
  const handleZoomToFit = useCallback(() => {
    if (!canvasRef.current) return

    try {
      // Use React Flow API's fitView (with 50px padding)
      canvasRef.current.fitView({ padding: 0.1 })
    } catch (error) {
      console.warn('Canvas not ready for zoom to fit:', error)
    }
  }, [canvasRef])

  // Zoom controls - use MagicCanvas API directly
  const handleZoomIn = useCallback(() => {
    if (!canvasRef.current) return

    try {
      // Use MagicCanvas API's built-in zoomIn (zooms at center)
      canvasRef.current.zoomIn()
    } catch (error) {
      console.warn('Canvas not ready for zoom in:', error)
    }
  }, [canvasRef])

  const handleZoomOut = useCallback(() => {
    if (!canvasRef.current) return

    try {
      // Use MagicCanvas API's built-in zoomOut (zooms at center)
      canvasRef.current.zoomOut()
    } catch (error) {
      console.warn('Canvas not ready for zoom out:', error)
    }
  }, [canvasRef])

  const handleZoomReset = useCallback(() => {
    if (!canvasRef.current) return

    try {
      // Reset to 100% zoom (1.0)
      canvasRef.current.zoomTo(1)
    } catch (error) {
      console.warn('Canvas not ready for zoom reset:', error)
    }
  }, [canvasRef])

  // Delete canvas
  const handleDelete = useCallback(() => {
    setShowDeleteDialog(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (currentCanvas) {
      try {
        posthog.capture('canvas_deleted', {
          canvas_id: currentCanvas.uuid,
          canvas_title: currentCanvas.title,
        })
        await deleteCanvas(currentCanvas.uuid)
        navigate({ to: '/workspace' })
      } catch (error) {
        console.error('Failed to delete canvas:', error)
        setShowDeleteDialog(false)
      }
    }
  }, [currentCanvas, deleteCanvas, navigate, posthog])

  // Filter out current project from move options
  const currentProjectUuid = currentCanvas?.project?.uuid
  const availableProjects = useMemo(
    () => projects.filter(p => p.uuid !== currentProjectUuid),
    [projects, currentProjectUuid],
  )

  // Canvas actions object for dropdown menu
  const canvasActions: CanvasActions = useMemo(() => ({
    onBackToWorkspace: handleBackToWorkspace,
    onNewCanvas: handleNewCanvas,
    onDuplicate: handleDuplicate,
    onRename: handleRename,
    onMoveToProject: handleMoveToProject,
    onZoomToFit: handleZoomToFit,
    onZoomIn: handleZoomIn,
    onZoomOut: handleZoomOut,
    onZoomReset: handleZoomReset,
    currentZoom,
    onDelete: handleDelete,
    availableProjects,
    showControls: viewToggles?.showControls,
    onToggleControls: viewToggles?.onToggleControls,
    showMiniMap: viewToggles?.showMiniMap,
    onToggleMiniMap: viewToggles?.onToggleMiniMap,
  }), [
    handleBackToWorkspace,
    handleNewCanvas,
    handleDuplicate,
    handleRename,
    handleMoveToProject,
    handleZoomToFit,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    currentZoom,
    handleDelete,
    availableProjects,
    viewToggles?.showControls,
    viewToggles?.onToggleControls,
    viewToggles?.showMiniMap,
    viewToggles?.onToggleMiniMap,
  ])

  return {
    // Actions
    canvasActions,
    handleRenameConfirm,
    handleDeleteConfirm,
    handleDuplicateConfirm,
    handleNewCanvasConfirm,

    // Modal states
    showRenameDialog,
    setShowRenameDialog,
    showDeleteDialog,
    setShowDeleteDialog,
    showDuplicateDialog,
    setShowDuplicateDialog,
    showNewCanvasDialog,
    setShowNewCanvasDialog,
    newCanvasName,
    setNewCanvasName,
  }
}
